import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Clock,
  CalendarDays,
  Loader2,
  CheckCircle2,
  RotateCcw,
  MailCheck,
} from "lucide-react";
import { WEEKDAY_SLOTS, SUNDAY_SLOT, SERVICES } from "../data/content";
import { Kicker } from "./fx";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  service: "",
  date: "",
  slot: "",
  notes: "",
};

export default function BookingSection() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const today = new Date().toISOString().split("T")[0];
  const isSunday = form.date
    ? new Date(`${form.date}T00:00:00`).getDay() === 0
    : false;
  const slots = isSunday ? [SUNDAY_SLOT] : WEEKDAY_SLOTS;

  useEffect(() => {
    const handler = (e) =>
      setForm((f) => ({ ...f, service: e.detail || f.service }));
    window.addEventListener("ndc:select-service", handler);
    return () => window.removeEventListener("ndc:select-service", handler);
  }, []);

  const set = (k) => (e) => {
    const value = e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: value };
      if (k === "date") {
        const sunday = value
          ? new Date(`${value}T00:00:00`).getDay() === 0
          : false;
        const allowed = sunday ? [SUNDAY_SLOT] : WEEKDAY_SLOTS;
        if (f.slot && !allowed.includes(f.slot)) next.slot = "";
        if (sunday) next.slot = SUNDAY_SLOT;
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) return toast.error("Please choose a service");
    if (!form.date) return toast.error("Please pick a preferred date");
    if (!form.slot) return toast.error("Please choose a time slot");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/appointments`, form);
      setResult({ ...data, ...form });
      toast.success("Appointment request sent to the clinic");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Could not submit right now. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="book"
      className="relative bg-gradient-to-b from-[#070B12] to-[#0A1220] py-24 sm:py-32"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-0 h-[300px] w-[600px] -translate-x-1/2 rounded-full bg-toothcyan/[0.05] blur-3xl"
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 sm:px-8 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="lg:col-span-5"
        >
          <Kicker>Working Hours</Kicker>
          <h2 className="font-serif text-3xl font-light tracking-tight text-white sm:text-5xl">
            Reserve your chair,{" "}
            <em className="italic text-ember">skip the wait.</em>
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-mist">
            Pick a service, a date and a slot — your request lands directly in
            Dr. Panda's inbox and the clinic confirms your visit personally.
          </p>

          <div
            data-testid="working-hours-card"
            className="grain relative mt-9 overflow-hidden rounded-[2rem] border border-white/10 bg-navy-deep text-ivory shadow-[0_25px_70px_rgba(0,0,0,0.5)]"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-ember/15 blur-2xl"
            />
            <div className="relative flex items-center gap-3 border-b border-white/10 px-6 py-4">
              <Clock className="h-4 w-4 text-ember" />
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ivory">
                Clinic Timings
              </span>
            </div>
            <div className="relative divide-y divide-white/10">
              <div className="flex items-center justify-between px-6 py-5">
                <div>
                  <div className="font-serif text-base font-semibold text-ivory">
                    Monday – Saturday
                  </div>
                  <div className="mt-0.5 text-xs text-ivory/50">
                    Regular clinic hours
                  </div>
                </div>
                <span className="rounded-full bg-gradient-to-r from-ember to-amber-500 px-4 py-1.5 font-mono text-[11px] font-bold text-[#070B12]">
                  11:00 AM – 08:00 PM
                </span>
              </div>
              <div className="flex items-center justify-between px-6 py-5">
                <div>
                  <div className="font-serif text-base font-semibold text-ivory">
                    Sunday
                  </div>
                  <div className="mt-0.5 text-xs text-ivory/50">
                    Dedicated pre-booked slots
                  </div>
                </div>
                <span className="rounded-full border border-white/15 bg-white/5 px-4 py-1.5 font-mono text-[11px] font-semibold text-ivory/90">
                  On Appointment Basis
                </span>
              </div>
            </div>
            <div className="relative flex items-center gap-2.5 border-t border-white/10 bg-navy-soft/40 px-6 py-4">
              <MailCheck className="h-4 w-4 shrink-0 text-ember" />
              <p className="text-xs leading-relaxed text-ivory/60">
                Every booking notifies{" "}
                <span className="font-semibold text-ember">
                  drswagatndc@gmail.com
                </span>{" "}
                instantly with your full details.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.12, ease: EASE }}
          className="lg:col-span-7"
        >
          <div className="rounded-[2rem] border border-toothcyan/20 bg-[#0F192C] p-6 shadow-[0_25px_70px_rgba(0,0,0,0.5),0_0_40px_rgba(23,210,245,0.05)] ring-1 ring-white/5 sm:p-10">
            <AnimatePresence mode="wait">
              {result ? (
                <motion.div
                  key="confirmation"
                  data-testid="booking-confirmation-card"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="text-center"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.15 }}
                    className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 shadow-[0_0_30px_rgba(16,185,129,0.25)]"
                  >
                    <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                  </motion.div>
                  <h3 className="mt-5 font-serif text-2xl font-semibold text-white sm:text-3xl">
                    Request received, {result.name.split(" ")[0]}.
                  </h3>
                  <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-mist">
                    Your appointment request has been emailed to the clinic
                    {result.patient_email_sent
                      ? " and a copy has been sent to your inbox"
                      : ""}
                    . Dr. Panda's team will confirm your slot shortly.
                  </p>
                  <div className="mx-auto mt-6 max-w-sm rounded-2xl border border-white/10 bg-[#070B12] p-5 text-left">
                    <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-3">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist">
                        Booking Reference
                      </span>
                      <span
                        data-testid="booking-reference-id"
                        className="font-mono text-sm font-bold text-ember"
                      >
                        {result.booking_ref}
                      </span>
                    </div>
                    {[
                      ["Service", result.service],
                      ["Date", result.date],
                      ["Slot", result.slot],
                    ].map(([l, v]) => (
                      <div
                        key={l}
                        className="flex items-center justify-between py-1.5 text-sm"
                      >
                        <span className="text-mist">{l}</span>
                        <span className="font-semibold text-ivory">{v}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    data-testid="book-another-appointment-button"
                    onClick={() => {
                      setResult(null);
                      setForm(EMPTY);
                    }}
                    className="mt-7 inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-ivory transition-all duration-200 hover:border-ember hover:text-ember active:scale-[0.97]"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Book another appointment
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="appointment-booking-form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.4 }}
                  onSubmit={submit}
                >
                  <div className="mb-7 flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-navy-deep">
                      <CalendarDays className="h-5 w-5 text-ember" />
                    </span>
                    <div>
                      <h3 className="font-serif text-xl font-semibold text-white">
                        Book an Appointment
                      </h3>
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-mist">
                        Response within clinic hours
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label className="field-label" htmlFor="appt-name">
                        Full Name
                      </label>
                      <input
                        id="appt-name"
                        data-testid="appointment-patient-name-input"
                        className="field-input"
                        placeholder="Your full name"
                        required
                        minLength={2}
                        value={form.name}
                        onChange={set("name")}
                      />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="appt-phone">
                        Phone / WhatsApp
                      </label>
                      <input
                        id="appt-phone"
                        data-testid="appointment-patient-phone-input"
                        className="field-input"
                        type="tel"
                        placeholder="e.g. 98765 43210"
                        required
                        minLength={7}
                        value={form.phone}
                        onChange={set("phone")}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="field-label" htmlFor="appt-email">
                        Email Address
                      </label>
                      <input
                        id="appt-email"
                        data-testid="appointment-patient-email-input"
                        className="field-input"
                        type="email"
                        placeholder="you@example.com"
                        required
                        value={form.email}
                        onChange={set("email")}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="field-label" htmlFor="appt-service">
                        Select Treatment
                      </label>
                      <select
                        id="appt-service"
                        data-testid="appointment-service-select"
                        className="field-input"
                        required
                        value={form.service}
                        onChange={set("service")}
                      >
                        <option value="" disabled>
                          Choose from 11 specialities
                        </option>
                        {SERVICES.map((s) => (
                          <option key={s.name} value={s.name}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="field-label" htmlFor="appt-date">
                        Preferred Date
                      </label>
                      <input
                        id="appt-date"
                        data-testid="appointment-date-input"
                        className="field-input"
                        type="date"
                        min={today}
                        required
                        value={form.date}
                        onChange={set("date")}
                      />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="appt-slot">
                        Preferred Time Slot
                      </label>
                      <select
                        id="appt-slot"
                        data-testid="appointment-time-slot-select"
                        className="field-input"
                        required
                        value={form.slot}
                        onChange={set("slot")}
                      >
                        <option value="" disabled>
                          {isSunday
                            ? "Sunday — on appointment basis"
                            : "11:00 AM – 08:00 PM"}
                        </option>
                        {slots.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="field-label" htmlFor="appt-notes">
                        Concern / Notes (optional)
                      </label>
                      <textarea
                        id="appt-notes"
                        data-testid="appointment-notes-input"
                        className="field-input min-h-[92px] resize-y"
                        placeholder="Briefly describe your dental concern…"
                        maxLength={800}
                        value={form.notes}
                        onChange={set("notes")}
                      />
                    </div>
                  </div>

                  <motion.button
                    data-testid="appointment-submit-button"
                    type="submit"
                    disabled={loading}
                    whileTap={{ scale: 0.98 }}
                    className="mt-7 flex w-full items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-ember to-amber-500 px-7 py-4 text-sm font-bold text-[#070B12] shadow-[0_0_28px_rgba(232,178,92,0.25)] transition-shadow duration-300 hover:shadow-[0_0_42px_rgba(232,178,92,0.45)] disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending to the clinic…
                      </>
                    ) : (
                      <>
                        <CalendarDays className="h-4 w-4" />
                        Confirm Appointment Request
                      </>
                    )}
                  </motion.button>
                  <p className="mt-4 text-center text-[11px] leading-relaxed text-mist">
                    Your details go straight to the clinic inbox — never shared,
                    never used for marketing.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
